import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';

import { ActivatedRoute } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { upperCaseBrands } from '../../brand-format-list';
import { DisplayDetails } from '../claim-detail-container/claim-detail-container.component';
import { IrelandBasedCompanies } from './irish-companies';
import {
  CallCostType,
  CallCostTypeEnum,
  ManageMyRepairResponse,
  MMRClaimDetail,
  MMRDirectReferral,
  MMRItem,
} from '../../../types/manage-my-repair-types';
import {
  ClaimType,
  RepairAction,
  RepairActionEnum,
  ServiceAction,
  ServiceActionEnum,
} from '../../../types/plan-detail.types';
import { NoSpacesPipe } from '../../../pipes/noSpaces.pipe';
import { PlanDetailsTrackingService } from '../../../services/plan-details-tracking.service';
import { AppliancesService } from '../../../services/appliances.service';
import { catchError, finalize, of, switchMap } from 'rxjs';
import { PlanDocumentKeyResponse } from '../../../types/plan-document.model';
import { UnavailableDocumentsComponent } from './plan-docs/unavailable-docs.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'claims-athome-myaccount-manage-my-repair-non-active-repair-state',
  templateUrl: './non-active-repair-state.component.html',
  styleUrls: ['./non-active-repair-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageMyRepairNonActiveRepairStateComponent implements OnInit {
  @Input() item: MMRItem;

  @Input() action: RepairAction | ServiceAction;

  @Input() claimType: ClaimType;

  @Input() fullClaimsDetails: ManageMyRepairResponse;

  @Input() directReferral: MMRDirectReferral;

  @Input() claimDisplayDetails: DisplayDetails | undefined;

  @Input() claimDetails: MMRClaimDetail;

  @Input() isRPC: boolean;

  @Input() bookingOEM: string;

  @Input() productType: string;

  @Input() isNonBookableProductType: boolean;

  @Input() isSPToContactCustomerFlow: boolean;

  @Input() isNewClaims = false;

  @Output() cancelClaim: EventEmitter<void> = new EventEmitter<void>();

  @Output() rebookClaim: EventEmitter<void> = new EventEmitter<void>();

  @Output() createClaim: EventEmitter<void> = new EventEmitter<void>();

  repairActionEnum = RepairActionEnum;

  serviceActionEnum = ServiceActionEnum;

  isMobile = false;

  planNumber: string;

  isDownloadingDocs = false;

  get calculatedAction(): string {
    if (this.fullClaimsDetails.planInWaitPeriod && this.isNewClaims) {
      return RepairActionEnum.IN_WAIT;
    }

    if (this.fullClaimsDetails.planInWarrantyPeriod && this.isNewClaims) {
      return RepairActionEnum.IN_WARRANTY;
    }

    return this.action;
  }
  get formattedManufacturerString(): string {
    const manufacturerIsUpperCased = upperCaseBrands.includes(
      this.item.manufacturer
    );

    if (manufacturerIsUpperCased) {
      return this.item.manufacturer;
    }

    const lowerCasedText = this.item.manufacturer.toLowerCase();

    return `${lowerCasedText.charAt(0).toUpperCase()}${lowerCasedText.substring(
      1,
      lowerCasedText.length
    )}`;
  }

  get callUsNumber(): string {
    const oem = this.bookingOEM?.toLowerCase() ?? '';

    const isPlumbingOrDrainageItem = this.item?.code === 'X7';
    const nonBookableNumbers: Record<string, string> = {
      argos: '0333 000 4915',
      'john lewis': '0333 000 4994',
    };
    const schemes = ['4NT', '4NU', '4NV', '4NW'];

    if (
      this.productType === 'PD' &&
      (oem.includes('haier') ||
        oem.includes('hoover candy') ||
        schemes.some((scheme) => this.planNumber?.startsWith(scheme)))
    ) {
      return '0333 000 1410';
    }

    if (this.productType === 'FC') {
      return (
        Object.entries(nonBookableNumbers).find(([key]) =>
          oem.includes(key)
        )?.[1] ?? '0333 000 1415'
      );
    }

    if (this.isNonBookableProductType || isPlumbingOrDrainageItem) {
      return '0333 000 1415';
    }

    // In-warranty calls
    if (this.action === this.repairActionEnum.IN_WARRANTY) {
      return this.claimDisplayDetails?.config?.inWarrantyContactTel as string;
    }

    // Default: D&G call centre
    return this.directReferral?.company_phone ?? '0333 000 4966';
  }

  get phoneButtonLabel(): string {
    return (
      this.directReferral?.company_phone ||
      this.claimDisplayDetails?.config?.inWarrantyContactTel ||
      this.translateService.instant('MMR.CALL_US_BUTTON')
    );
  }

  get buttonIcon(): any {
    const isInWarranty = this.action === this.repairActionEnum.IN_WARRANTY;

    return this.directReferral?.company_phone || isInWarranty ? 'phone' : '';
  }

  get callType(): CallCostType {
    if (
      this.noSpaces
        .transform(this.directReferral.company_phone || '')
        .startsWith('0333')
    ) {
      return CallCostTypeEnum.STANDARD_MESSAGE;
    }

    if (this.directReferral.company_name) {
      const irishCompany = IrelandBasedCompanies.find(
        (i) =>
          i.toLowerCase() === this.directReferral.company_name.toLowerCase()
      );
      if (irishCompany) {
        return CallCostTypeEnum.IRELAND;
      }
    }

    return CallCostTypeEnum.UK;
  }

  constructor(
    private readonly noSpaces: NoSpacesPipe,
    private readonly translateService: TranslateService,
    private readonly planDetailsTrackingService: PlanDetailsTrackingService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly appliancesServices: AppliancesService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.checkScreenSize();
    this.planNumber = this.activatedRoute.snapshot.params.planNumber;
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 576;
  }

  handleDownloadPlan(): void {
    const planNumber =
      this.fullClaimsDetails?.parentPlanNumber ?? this.planNumber;

    if (!planNumber) {
      return;
    }

    this.isDownloadingDocs = true;
    this.cdr.markForCheck();

    this.planDetailsTrackingService.trackDocumentDownloadClick();
    this.appliancesServices
      .searchPlanDocumentKey(planNumber)
      .pipe(
        switchMap((response: PlanDocumentKeyResponse) =>
          this.appliancesServices.downloadPlanDocument(
            planNumber,
            response.result
          )
        ),
        catchError((error) => of(this.openFailureDialog(error))),
        finalize(() => {
          this.isDownloadingDocs = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);

      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
        ? parsedUrl.hostname.length > 0
        : false;
    } catch (error) {
      return false;
    }
  }

  isRequestAction(action: string): boolean {
    return (
      (action === this.repairActionEnum.REQUEST_REPAIR ||
        action === this.serviceActionEnum.REQUEST_SERVICE) &&
      !this.isNonBookableProductType &&
      !this.fullClaimsDetails.planInWaitPeriod
    );
  }

  openFailureDialog(error: Error) {
    const { message } = error;
    this.planDetailsTrackingService.trackDocumentDownloadError(`${message}`);
    this.dialog.open(UnavailableDocumentsComponent, {
      ...{
        panelClass: 'vertical-scroll',
      },
    });
  }
}
